import { IPositionCandidate, IPositionWithCandidates } from '@types';
import { ICandidatesViewState } from '@hooks';
import {
  CandidateListingStatusLabel,
  CandidateStageByStatus,
  CandidateStageKey,
  CandidateStageLabel,
  CandidateStatus,
  CandidateStatusOrder,
  SeniorityOrder,
} from '@constants';
import {
  CandidateRegion,
  CandidateRegionOrder,
  getCandidateRegion,
} from './candidateRegion';
import { normalizeCandidateStatus } from './candidate';
import { toggle } from './array';
import { formatSkillLabel } from './skill';
import { includesQuery } from './string';

const matchesQuery = (
  candidate: IPositionCandidate,
  position: IPositionWithCandidates,
  query: string,
): boolean => {
  return includesQuery(query, [
    `${candidate.firstName} ${candidate.lastName}`,
    candidate.seniority,
    candidate.country,
    position.name,
    position.workstream ?? '',
    ...(candidate.skills ?? []).flatMap((skill) => [
      skill,
      formatSkillLabel(skill),
    ]),
  ]);
};

const matchesFacets = (
  candidate: IPositionCandidate,
  state: ICandidatesViewState,
): boolean => {
  if (
    state.regions.length &&
    !state.regions.includes(getCandidateRegion(candidate.country))
  )
    return false;

  if (
    state.seniorities.length &&
    !isSenioritySelected(state.seniorities, candidate.seniority)
  )
    return false;

  if (
    state.statuses.length &&
    !state.statuses.includes(candidateFacetKey(candidate.status))
  )
    return false;

  return true;
};

export const applyCandidateFilters = (
  positions: IPositionWithCandidates[],
  state: ICandidatesViewState,
): IPositionWithCandidates[] =>
  positions
    .filter(
      (position) =>
        !state.positionIds.length || state.positionIds.includes(position.id),
    )
    .map((position) => ({
      ...position,
      candidates: position.candidates.filter(
        (candidate) =>
          matchesQuery(candidate, position, state.query) &&
          matchesFacets(candidate, state),
      ),
    }));

export interface IStatusFacet {
  key: string;
  label: string;
}

export const candidateFacetKey = (status: string): string => {
  const normalized = normalizeCandidateStatus(status);
  if (!normalized) return status;

  return CandidateStageByStatus[normalized] ?? normalized;
};

export const candidateFacetLabel = (key: string): string =>
  CandidateStageLabel[key as CandidateStageKey] ??
  CandidateListingStatusLabel[key as CandidateStatus] ??
  key;

const FacetOrder: string[] = Array.from(
  new Set(CandidateStatusOrder.map(candidateFacetKey)),
);

export const collectStatusFacets = (
  positions: IPositionWithCandidates[],
): IStatusFacet[] => {
  const keys = new Set<string>();

  positions.forEach(({ candidates }) =>
    candidates.forEach((candidate) => {
      if (candidate.status) keys.add(candidateFacetKey(candidate.status));
    }),
  );

  return Array.from(keys)
    .map((key) => ({ key, label: candidateFacetLabel(key) }))
    .sort((a, b) => FacetOrder.indexOf(a.key) - FacetOrder.indexOf(b.key));
};

export const regionOptions = (
  countries: (string | null)[],
): CandidateRegion[] => {
  const regions = new Set(countries.map(getCandidateRegion));

  return CandidateRegionOrder.filter((region) => regions.has(region));
};

export const collectRegions = (
  positions: IPositionWithCandidates[],
): CandidateRegion[] =>
  regionOptions(
    positions.flatMap(({ candidates }) =>
      candidates.map(({ country }) => country),
    ),
  );

const seniorityBase = (seniority: string): string => {
  const [base] = seniority.split(' ');

  return SeniorityOrder.includes(base) ? base : seniority;
};

export const isSenioritySelected = (
  selected: string[],
  seniority: string,
): boolean =>
  selected.includes(seniority) || selected.includes(seniorityBase(seniority));

export const toggleSeniority = (
  selected: string[],
  seniority: string,
  options: string[],
): string[] => {
  const base = seniorityBase(seniority);
  const children = options.filter(
    (option) => option !== base && seniorityBase(option) === base,
  );
  const rest = selected.filter((value) => seniorityBase(value) !== base);

  if (seniority === base)
    return selected.includes(base) ? rest : [...rest, base];

  const picked = toggle(
    children.filter((child) => isSenioritySelected(selected, child)),
    seniority,
  );

  return children.length > 1 && picked.length === children.length
    ? [...rest, base]
    : [...rest, ...picked];
};

export const seniorityOptions = (seniorities: (string | null)[]): string[] => {
  const values = Array.from(
    new Set(seniorities.filter((value): value is string => Boolean(value))),
  );
  const families = values
    .map(seniorityBase)
    .filter((base, index, bases) => bases.indexOf(base) !== index);

  return Array.from(new Set([...values, ...families]));
};

export const collectSeniorities = (
  positions: IPositionWithCandidates[],
): string[] =>
  seniorityOptions(
    positions.flatMap(({ candidates }) =>
      candidates.map(({ seniority }) => seniority),
    ),
  );
